// Bank — Konto verbinden, Umsätze sehen, Zahlungseingänge den Rechnungen zuordnen.
//
// Nur lesen: Die Verbindung ist eine Kontoinformations-Zustimmung bei der
// Bank. Die Anmeldung passiert auf der Seite der Bank, nicht hier — LagerPro
// bekommt die Zugangsdaten nie zu sehen. Das steht auf der Seite so deutlich,
// weil es die erste Frage jedes Kunden ist.
//
// Die meisten Eingänge ordnet die App selbst zu (Rechnungsnummer, Name,
// Betrag). Was sie nicht sicher weiß, schlägt sie vor; ein Klick bestätigt.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum, datumZeit } from '../../../gemeinsam/lib/daten';
import {
  Abzeichen, Auswahl, Blickfang, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner, TarifSperre,
} from '../../../gemeinsam/komponenten/Basis';

type Konto = { uid: string; iban: string; name: string; waehrung: string; stand: number | null; standAm: string | null };
type Verbindung = {
  id: string; bank: { name: string; land: string }; psuTyp: 'business' | 'personal';
  status: 'aktiv' | 'laeuftAb' | 'abgelaufen' | 'getrennt';
  gueltigBis: string | null; tageUebrig: number | null; konten: Konto[];
  angelegtAm: string; letzterAbruf: string | null; letzterFehler: { am: string; meldung: string } | null;
};
type Status = {
  modus: 'live' | 'test' | 'aus';
  verbindungen: Verbindung[];
  zaehler: { offen: number; automatisch: number; umsaetze: number };
  ersterAbrufTage: number;
  land: string;
};
type Posten = { rechnungId: string; nummer: string; kundeName: string; betrag: number };
type Vorschlag = { rechnungId: string; punkte: number; gruende: string[]; nummer: string; kundeName: string; offen: number };
type Umsatz = {
  id: string; datum: string; betrag: number; waehrung: string;
  gegenName: string; gegenIban: string; zweck: string;
  konto: { iban: string; name: string; bank: string };
  ignoriert: boolean;
  zuordnung: null | {
    posten: Posten[]; regel: string; regelText: string; automatisch: boolean; ueberzahlung: number; am: string;
  };
  vorschlaege: Vorschlag[];
};
type OffeneRechnung = { id: string; nummer: string; kundeName: string; offen: number; brutto: number; datum: string };
type Bankeintrag = { name: string; land: string; logo: string | null; psuTypen: string[]; maxTage: number; beta: boolean };
type Ansicht = 'offen' | 'zugeordnet' | 'ausgaenge' | 'ignoriert' | 'alle';
type Meldung = { art: 'erfolg' | 'fehler' | 'info'; text: string };

// Länder, für die Enable Banking Banken anbietet — die häufigsten zuerst.
const LAENDER: Array<[string, string]> = [
  ['DE', 'Deutschland'], ['AT', 'Österreich'], ['NL', 'Niederlande'], ['BE', 'Belgien'], ['LU', 'Luxemburg'],
  ['FR', 'Frankreich'], ['ES', 'Spanien'], ['IT', 'Italien'], ['PT', 'Portugal'], ['IE', 'Irland'],
  ['FI', 'Finnland'], ['SE', 'Schweden'], ['DK', 'Dänemark'], ['NO', 'Norwegen'], ['PL', 'Polen'],
  ['CZ', 'Tschechien'], ['EE', 'Estland'], ['LV', 'Lettland'], ['LT', 'Litauen'],
];

const iban = (s: string) => (s || '').replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
const fehlerText = (e: unknown, sonst: string) => (e instanceof ApiFehler ? e.message : sonst);

export default function BankSeite() {
  const ort = useLocation();
  const nav = useNavigate();
  const { daten: status, laedt, fehler, tarifsperre, neuLaden } = useDaten<Status>('/bank/status');
  const [ansicht, setAnsicht] = useState<Ansicht>('offen');
  const liste = useDaten<{ umsaetze: Umsatz[] }>(`/bank/umsaetze?ansicht=${ansicht}`, [ansicht]);
  const listeNeuLaden = liste.neuLaden;
  const [meldung, setMeldung] = useState<Meldung | null>((ort.state as { meldung?: Meldung } | null)?.meldung ?? null);
  const [bankwahl, setBankwahl] = useState(false);
  const [ruft, setRuft] = useState(false);
  const [rueckkehr, setRueckkehr] = useState(ort.pathname.endsWith('/rueckkehr'));

  // Zurück von der Bank: Code einlösen, dann auf die normale Adresse — sonst
  // würde ein Neuladen den schon verbrauchten Code noch einmal schicken.
  const eingeloest = useRef(false);
  useEffect(() => {
    if (!ort.pathname.endsWith('/rueckkehr') || eingeloest.current) return;
    eingeloest.current = true;
    const q = new URLSearchParams(ort.search);
    (async () => {
      let m: Meldung;
      try {
        const r = await api<{
          verbindung: Verbindung;
          abruf: { neu: number; eingaenge: number; automatisch: number; offen: number } | null;
          abrufFehler: string | null;
        }>('/bank/rueckkehr', {
          methode: 'POST',
          daten: { state: q.get('state') || '', code: q.get('code'), fehler: q.get('error') || q.get('error_description') },
        });
        m = {
          art: 'erfolg',
          text: `${r.verbindung.bank.name} ist verbunden. `
            + (r.abruf
              ? `${r.abruf.neu} Umsätze geholt, ${r.abruf.automatisch} Zahlungseingänge automatisch Rechnungen zugeordnet`
                + (r.abruf.offen ? `, ${r.abruf.offen} ${r.abruf.offen === 1 ? 'wartet' : 'warten'} auf Ihre Entscheidung.` : '.')
              : `Die Umsätze kommen beim nächsten Abruf. ${r.abrufFehler || ''}`),
        };
      } catch (e) {
        m = { art: 'fehler', text: fehlerText(e, 'Die Verbindung zur Bank ist fehlgeschlagen.') };
      }
      setRueckkehr(false);
      setMeldung(m);
      nav('/bank', { replace: true, state: { meldung: m } });
      await Promise.all([neuLaden(), listeNeuLaden()]);
    })();
  }, [ort.pathname, ort.search, nav, neuLaden, listeNeuLaden]);

  if (tarifsperre) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Bank</h1>
        <TarifSperre was="Die Bankanbindung" upgradeAuf={tarifsperre.upgradeAuf} />
      </div>
    );
  }
  if (rueckkehr) {
    return (
      <div className="flex flex-col items-center gap-3 py-20 text-sm text-slate-600 dark:text-slate-300">
        <Spinner />
        Verbindung zur Bank wird eingerichtet und die Umsätze werden geholt …
      </div>
    );
  }
  if (laedt && !status) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (fehler || !status) return <Hinweis art="fehler">{fehler || 'Die Bankdaten konnten nicht geladen werden.'}</Hinweis>;

  const verbunden = status.verbindungen.length > 0;

  async function abrufen() {
    setRuft(true); setMeldung(null);
    try {
      const r = await api<{ neu: number; automatisch: number; offen: number; fehler: Array<{ bank: string; meldung: string }> }>(
        '/bank/abrufen', { methode: 'POST' });
      setMeldung(r.fehler.length
        ? { art: 'fehler', text: r.fehler.map(f => `${f.bank}: ${f.meldung}`).join(' · ') }
        : {
          art: 'erfolg',
          text: r.neu
            ? `${r.neu} neue Umsätze, ${r.automatisch} automatisch zugeordnet.`
            : r.automatisch ? `${r.automatisch} Eingänge automatisch zugeordnet.` : 'Keine neuen Umsätze.',
        });
      await Promise.all([neuLaden(), liste.neuLaden()]);
    } catch (e) {
      setMeldung({ art: 'fehler', text: fehlerText(e, 'Abruf fehlgeschlagen.') });
    } finally { setRuft(false); }
  }

  async function verbinden(bank: string, land: string, psuTyp: string, erneuern?: string) {
    setMeldung(null);
    try {
      const { url } = await api<{ url: string }>('/bank/verbinden', {
        methode: 'POST', daten: { bank, land, psuTyp, sprache: 'de', erneuern },
      });
      // Weiter zur Bank. Zurück kommt der Kunde über /bank/rueckkehr.
      window.location.href = url;
    } catch (e) {
      setMeldung({ art: 'fehler', text: fehlerText(e, 'Die Bank ist gerade nicht erreichbar.') });
    }
  }

  async function trennen(v: Verbindung) {
    if (!confirm(`Verbindung zu ${v.bank.name} trennen?\n\nDie Zustimmung bei der Bank wird beendet. `
      + 'Bereits geholte Umsätze und Zuordnungen bleiben erhalten; der Kontostand wird danach wieder von Hand gepflegt.')) return;
    try {
      await api(`/bank/verbindungen/${v.id}`, { methode: 'DELETE' });
      setMeldung({ art: 'info', text: `${v.bank.name} ist getrennt.` });
      await neuLaden();
    } catch (e) {
      setMeldung({ art: 'fehler', text: fehlerText(e, 'Trennen fehlgeschlagen.') });
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Bank</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Kontostand und Umsätze ansehen — Zahlungseingänge ordnen sich den Rechnungen selbst zu.
          </p>
        </div>
        {verbunden && status.modus !== 'aus' && (
          <div className="flex gap-2">
            <Knopf onClick={() => setBankwahl(true)}>Weitere Bank</Knopf>
            <Knopf variante="primaer" laedt={ruft} onClick={abrufen}>Jetzt abrufen</Knopf>
          </div>
        )}
      </div>

      {status.modus === 'test' && (
        <Hinweis art="warnung" titel="Testmodus">
          Simulierte Bank, keine echten Kontodaten. Die Zuordnungen wirken trotzdem auf die Rechnungen dieses Kontos.
        </Hinweis>
      )}
      {meldung && <Blickfang><Hinweis art={meldung.art}>{meldung.text}</Hinweis></Blickfang>}

      {status.modus === 'aus' ? (
        <Hinweis art="info" titel="Bald verfügbar">
          Die Bankanbindung wird gerade eingerichtet. Bis dahin lässt sich der Kontostand in der Buchhaltung unter
          Finanzlage von Hand eintragen.
        </Hinweis>
      ) : bankwahl || !verbunden ? (
        <BankWahl
          land={status.land} erste={!verbunden}
          beiWahl={(b, land, psuTyp) => verbinden(b, land, psuTyp)}
          beiAbbruch={verbunden ? () => setBankwahl(false) : undefined}
        />
      ) : null}

      {status.verbindungen.map(v => (
        <VerbindungKarte key={v.id} v={v}
                         beiErneuern={() => verbinden(v.bank.name, v.bank.land, v.psuTyp, v.id)}
                         beiTrennen={() => trennen(v)} />
      ))}

      {(verbunden || status.zaehler.umsaetze > 0) && (
        <Umsaetze ansicht={ansicht} setAnsicht={setAnsicht} zaehler={status.zaehler}
                  laedt={liste.laedt} umsaetze={liste.daten?.umsaetze ?? []}
                  beiAenderung={async (m) => { if (m) setMeldung(m); await Promise.all([neuLaden(), liste.neuLaden()]); }} />
      )}
    </div>
  );
}

// ── Bank auswählen ───────────────────────────────────────────────────────────

function BankWahl({ land: start, erste, beiWahl, beiAbbruch }: {
  land: string; erste: boolean;
  beiWahl: (bank: string, land: string, psuTyp: string) => Promise<void>;
  beiAbbruch?: () => void;
}) {
  const [land, setLand] = useState(LAENDER.some(([c]) => c === start) ? start : 'DE');
  const [psuTyp, setPsuTyp] = useState<'business' | 'personal'>('business');
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const { daten, laedt, fehler } = useDaten<{ banken: Bankeintrag[] }>(`/bank/banken?land=${land}`, [land]);

  const treffer = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return (daten?.banken ?? [])
      .filter(b => b.psuTypen.includes(psuTyp))
      .filter(b => !s || b.name.toLowerCase().includes(s))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [daten, suche, psuTyp]);

  return (
    <Karte>
      <KartenKopf titel={erste ? 'Bankkonto verbinden' : 'Weitere Bank verbinden'}
                  aktion={beiAbbruch && <Knopf variante="still" onClick={beiAbbruch}>Abbrechen</Knopf>} />
      <div className="space-y-4 p-5">
        {erste && (
          <ol className="grid gap-3 text-sm text-slate-600 sm:grid-cols-3 dark:text-slate-300">
            {[
              ['Bank wählen', 'Sie werden zur Anmeldeseite Ihrer Bank weitergeleitet.'],
              ['Bei der Bank anmelden', 'Mit Ihren gewohnten Zugangsdaten und der Freigabe (TAN, App).'],
              ['Fertig', 'Umsätze der letzten 90 Tage kommen sofort, danach alle sechs Stunden.'],
            ].map(([t, x], i) => (
              <li key={t} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                <span className="mb-1 flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-marke-100 text-xs text-marke-700 dark:bg-marke-950 dark:text-marke-300">{i + 1}</span>
                  {t}
                </span>
                {x}
              </li>
            ))}
          </ol>
        )}
        <Hinweis art="info" titel="Nur lesen — Ihre Zugangsdaten sehen wir nie">
          Die Anmeldung findet auf der Seite Ihrer Bank statt. LagerPro erhält über den lizenzierten Dienst
          Enable Banking nur Lesezugriff auf Kontostand und Umsätze. Überweisungen sind über diese Verbindung nicht
          möglich. Die Zustimmung gilt bis zu 180 Tage und lässt sich jederzeit trennen.
        </Hinweis>

        <div className="grid gap-3 sm:grid-cols-[12rem_1fr_auto]">
          <Auswahl label="Land" value={land} onChange={e => { setLand(e.target.value); setSuche(''); }}>
            {LAENDER.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </Auswahl>
          <Feld label="Bank suchen" value={suche} placeholder="z. B. Sparkasse, Volksbank, ING …"
                onChange={e => setSuche(e.target.value)} />
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Konto</span>
            <div className="inline-flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
              {([['business', 'Geschäftlich'], ['personal', 'Privat']] as const).map(([id, n]) => (
                <button key={id} type="button" onClick={() => setPsuTyp(id)}
                        className={`rounded-md px-3 py-1.5 text-sm ${psuTyp === id
                          ? 'bg-marke-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>

        {laedt && !daten ? <div className="flex justify-center py-6"><Spinner /></div>
          : fehler ? <Hinweis art="fehler">{fehler}</Hinweis>
          : treffer.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
              Keine passende Bank gefunden{psuTyp === 'business' ? ' — manche Banken führen Geschäftskonten unter „Privat"' : ''}.
            </p>
          ) : (
            <ul className="grid max-h-[26rem] gap-2 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
              {treffer.slice(0, 120).map(b => (
                <li key={b.name}>
                  <button type="button" disabled={!!gewaehlt}
                          onClick={async () => { setGewaehlt(b.name); await beiWahl(b.name, land, psuTyp); setGewaehlt(null); }}
                          className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-left text-sm hover:border-marke-400 hover:bg-marke-50 disabled:opacity-60 dark:border-slate-700 dark:hover:bg-slate-800">
                    {b.logo
                      ? <img src={b.logo} alt="" className="h-7 w-7 shrink-0 rounded object-contain" loading="lazy" />
                      : <span className="grid h-7 w-7 shrink-0 place-items-center rounded bg-slate-100 text-xs font-semibold text-slate-500 dark:bg-slate-800">{b.name.slice(0, 1)}</span>}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-900 dark:text-slate-100">{b.name}</span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                        {gewaehlt === b.name ? 'Weiter zur Bank …' : `Zustimmung bis ${b.maxTage} Tage${b.beta ? ' · Beta' : ''}`}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
      </div>
    </Karte>
  );
}

// ── Verbindung ───────────────────────────────────────────────────────────────

function VerbindungKarte({ v, beiErneuern, beiTrennen }: { v: Verbindung; beiErneuern: () => void; beiTrennen: () => void }) {
  const abzeichen = {
    aktiv: <Abzeichen art="gruen">verbunden bis {datum(v.gueltigBis)}</Abzeichen>,
    laeuftAb: <Abzeichen art="gelb">endet in {v.tageUebrig} {v.tageUebrig === 1 ? 'Tag' : 'Tagen'}</Abzeichen>,
    abgelaufen: <Abzeichen art="rot">Zustimmung abgelaufen</Abzeichen>,
    getrennt: <Abzeichen>getrennt</Abzeichen>,
  }[v.status];

  return (
    <Karte>
      <KartenKopf
        titel={v.bank.name}
        hinweis={<>{v.psuTyp === 'business' ? 'Geschäftskonto' : 'Privatkonto'} · {abzeichen}</>}
        aktion={
          <div className="flex gap-2">
            {(v.status === 'laeuftAb' || v.status === 'abgelaufen') && (
              <Knopf variante="primaer" className="!text-xs" onClick={beiErneuern}>
                {v.status === 'abgelaufen' ? 'Neu verbinden' : 'Erneuern'}
              </Knopf>
            )}
            <Knopf variante="still" className="!text-xs" onClick={beiTrennen}>Trennen</Knopf>
          </div>
        } />
      <ul className="divide-y divide-slate-200 dark:divide-slate-800">
        {v.konten.map(k => (
          <li key={k.uid} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{k.name}</p>
              <p className="font-mono text-xs text-slate-500 dark:text-slate-400">{iban(k.iban) || '—'}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                {k.stand === null ? '—' : euro(k.stand)}
              </p>
              {k.standAm && <p className="text-xs text-slate-500 dark:text-slate-400">Stand {datum(k.standAm)}</p>}
            </div>
          </li>
        ))}
      </ul>
      <div className="border-t border-slate-200 px-5 py-2.5 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
        {v.letzterAbruf ? `Zuletzt abgerufen ${datumZeit(v.letzterAbruf)}` : 'Noch nicht abgerufen'}
        {' · '}der Kontostand fließt automatisch in die Finanzlage
        {v.letzterFehler && (
          <span className="mt-1 block text-red-600 dark:text-red-400">
            Letzter Abruf fehlgeschlagen ({datumZeit(v.letzterFehler.am)}): {v.letzterFehler.meldung}
          </span>
        )}
      </div>
    </Karte>
  );
}

// ── Umsätze ──────────────────────────────────────────────────────────────────

function Umsaetze({ ansicht, setAnsicht, zaehler, laedt, umsaetze, beiAenderung }: {
  ansicht: Ansicht; setAnsicht: (a: Ansicht) => void;
  zaehler: Status['zaehler']; laedt: boolean; umsaetze: Umsatz[];
  beiAenderung: (m?: Meldung) => Promise<void>;
}) {
  const reiter: Array<[Ansicht, string]> = [
    ['offen', `Offene Eingänge${zaehler.offen ? ` (${zaehler.offen})` : ''}`],
    ['zugeordnet', 'Zugeordnet'], ['ausgaenge', 'Ausgänge'], ['ignoriert', 'Ignoriert'], ['alle', 'Alle'],
  ];
  const leer: Record<Ansicht, string> = {
    offen: 'Alle Zahlungseingänge sind zugeordnet.',
    zugeordnet: 'Noch keine Zahlung zugeordnet.',
    ausgaenge: 'Keine Ausgänge im abgerufenen Zeitraum.',
    ignoriert: 'Nichts ignoriert.',
    alle: 'Noch keine Umsätze.',
  };

  return (
    <Karte>
      <KartenKopf titel="Umsätze"
                  hinweis={zaehler.automatisch ? `${zaehler.automatisch} Eingänge hat die App selbst zugeordnet` : undefined} />
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 px-3 dark:border-slate-800">
        {reiter.map(([id, name]) => (
          <button key={id} onClick={() => setAnsicht(id)}
                  className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm ${ansicht === id
                    ? 'border-marke-600 font-medium text-marke-700 dark:text-marke-300'
                    : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}>
            {name}
          </button>
        ))}
      </div>
      {laedt && !umsaetze.length ? <div className="flex justify-center py-10"><Spinner /></div>
        : umsaetze.length === 0 ? <LeerZustand titel={leer[ansicht]} />
        : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {umsaetze.map(u => <UmsatzZeile key={u.id} u={u} beiAenderung={beiAenderung} />)}
          </ul>
        )}
      {ansicht === 'offen' && (
        <details className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
          <summary className="cursor-pointer">Was die App selbst zuordnet</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            <li>Rechnungsnummer im Verwendungszweck und der Betrag stimmt.</li>
            <li>Rechnungsnummer und Name des Zahlers passen, Betrag kleiner — als Teilzahlung, die Rechnung bleibt offen.</li>
            <li>Mehrere Rechnungsnummern in einer Überweisung, wenn die Summe genau stimmt.</li>
            <li>Ohne Nummer: Name passt und genau eine offene Rechnung dieses Kunden hat diesen Betrag.</li>
          </ul>
          <p className="mt-2">Alles andere landet hier als Vorschlag. Eine falsche Zuordnung lässt sich jederzeit lösen.</p>
        </details>
      )}
    </Karte>
  );
}

function UmsatzZeile({ u, beiAenderung }: { u: Umsatz; beiAenderung: (m?: Meldung) => Promise<void> }) {
  const [waehlen, setWaehlen] = useState(false);
  const [arbeitet, setArbeitet] = useState(false);
  const eingang = u.betrag > 0;

  async function aktion(pfad: string, daten: unknown, erfolg: string) {
    setArbeitet(true);
    try {
      await api(`/bank/umsaetze/${u.id}/${pfad}`, { methode: 'POST', daten });
      setWaehlen(false);
      await beiAenderung({ art: 'erfolg', text: erfolg });
    } catch (e) {
      await beiAenderung({ art: 'fehler', text: fehlerText(e, 'Das hat nicht geklappt.') });
    } finally { setArbeitet(false); }
  }

  return (
    <li className="px-5 py-3.5">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {u.gegenName || (eingang ? 'Unbekannter Zahler' : 'Unbekannter Empfänger')}
            <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">{datum(u.datum)}</span>
          </p>
          {u.zweck && <p className="line-clamp-2 break-words text-sm text-slate-600 dark:text-slate-300">{u.zweck}</p>}
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {u.gegenIban && <span className="font-mono">{iban(u.gegenIban)} · </span>}
            {u.konto.bank}{u.konto.name ? ` · ${u.konto.name}` : ''}
          </p>
        </div>
        <p className={`text-base font-semibold tabular-nums ${eingang ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-700 dark:text-slate-300'}`}>
          {eingang ? '+' : ''}{euro(u.betrag)}
        </p>
      </div>

      {u.zuordnung ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {u.zuordnung.posten.map(p => (
            <Abzeichen key={p.rechnungId} art="gruen">✓ {p.nummer} · {p.kundeName} · {euro(p.betrag)}</Abzeichen>
          ))}
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {u.zuordnung.automatisch ? 'automatisch: ' : ''}{u.zuordnung.regelText}
            {u.zuordnung.ueberzahlung > 0 && ` · ${euro(u.zuordnung.ueberzahlung)} mehr gezahlt als offen`}
          </span>
          <Knopf variante="still" className="!px-2 !py-1 !text-xs" laedt={arbeitet}
                 onClick={() => aktion('loesen', {}, 'Zuordnung gelöst — die Rechnung ist wieder offen.')}>
            Lösen
          </Knopf>
        </div>
      ) : u.ignoriert ? (
        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          Ignoriert
          <Knopf variante="still" className="!px-2 !py-1 !text-xs" laedt={arbeitet}
                 onClick={() => aktion('ignorieren', { ignoriert: false }, 'Wieder bei den offenen Eingängen.')}>
            Wieder aufnehmen
          </Knopf>
        </div>
      ) : eingang ? (
        <div className="mt-2 space-y-2">
          {u.vorschlaege.map(v => (
            <div key={v.rechnungId}
                 className="flex flex-wrap items-center gap-2 rounded-lg border border-marke-200 bg-marke-50/60 px-3 py-2 text-sm dark:border-marke-900 dark:bg-marke-950/30">
              <span className="font-medium text-slate-900 dark:text-slate-100">{v.nummer}</span>
              <span className="text-slate-600 dark:text-slate-300">{v.kundeName} · offen {euro(v.offen)}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">{v.gruende.join(', ')}</span>
              <Knopf variante="primaer" className="ml-auto !px-2.5 !py-1 !text-xs" laedt={arbeitet}
                     onClick={() => aktion('zuordnen', { rechnungIds: [v.rechnungId] }, `Zugeordnet zu ${v.nummer}.`)}>
                Zuordnen
              </Knopf>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Knopf className="!px-2.5 !py-1 !text-xs" onClick={() => setWaehlen(!waehlen)}>
              {u.vorschlaege.length ? 'Andere Rechnung …' : 'Rechnung wählen …'}
            </Knopf>
            <Knopf variante="still" className="!px-2.5 !py-1 !text-xs" laedt={arbeitet}
                   onClick={() => aktion('ignorieren', { ignoriert: true }, 'Ignoriert — betrifft keine Rechnung.')}>
              Ignorieren
            </Knopf>
          </div>
          {waehlen && (
            <RechnungWahl betrag={u.betrag} arbeitet={arbeitet}
                          beiZuordnen={ids => aktion('zuordnen', { rechnungIds: ids }, ids.length > 1 ? `${ids.length} Rechnungen zugeordnet.` : 'Zugeordnet.')}
                          beiAbbruch={() => setWaehlen(false)} />
          )}
        </div>
      ) : null}
    </li>
  );
}

/** Rechnungen von Hand wählen — auch mehrere, für Sammelüberweisungen. */
function RechnungWahl({ betrag, arbeitet, beiZuordnen, beiAbbruch }: {
  betrag: number; arbeitet: boolean; beiZuordnen: (ids: string[]) => void; beiAbbruch: () => void;
}) {
  const { daten, laedt } = useDaten<OffeneRechnung[]>('/bank/offene-rechnungen');
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const s = suche.trim().toLowerCase();
  const liste = (daten ?? []).filter(r => !s || r.nummer.toLowerCase().includes(s) || r.kundeName.toLowerCase().includes(s));
  const summe = (daten ?? []).filter(r => gewaehlt.includes(r.id)).reduce((x, r) => x + r.offen, 0);

  return (
    <Blickfang>
      <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <Feld label="Offene Rechnung suchen" value={suche} autoFocus placeholder="Nummer oder Kunde"
              onChange={e => setSuche(e.target.value)} />
        {laedt && !daten ? <div className="flex justify-center py-4"><Spinner klein /></div> : (
          <ul className="mt-2 max-h-60 divide-y divide-slate-100 overflow-y-auto text-sm dark:divide-slate-800">
            {liste.length === 0 && <li className="py-3 text-center text-slate-500">Keine offene Rechnung gefunden.</li>}
            {liste.map(r => (
              <li key={r.id}>
                <label className="flex cursor-pointer items-center gap-3 py-2">
                  <input type="checkbox" checked={gewaehlt.includes(r.id)}
                         onChange={e => setGewaehlt(e.target.checked ? [...gewaehlt, r.id] : gewaehlt.filter(x => x !== r.id))} />
                  <span className="shrink-0 font-medium text-slate-900 sm:w-36 dark:text-slate-100">{r.nummer}</span>
                  <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">{r.kundeName}</span>
                  <span className="tabular-nums text-slate-700 dark:text-slate-200">{euro(r.offen)}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 dark:border-slate-800">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {gewaehlt.length
              ? `${gewaehlt.length} gewählt · offen ${euro(summe)} · Eingang ${euro(betrag)}`
                + (Math.abs(summe - betrag) < 0.005 ? ' · passt genau'
                  : betrag < summe ? ' · reicht nicht für alle, der Rest bleibt offen' : ` · ${euro(betrag - summe)} zu viel`)
              : 'In der gewählten Reihenfolge wird verteilt.'}
          </span>
          <div className="ml-auto flex gap-2">
            <Knopf variante="still" className="!text-xs" onClick={beiAbbruch}>Abbrechen</Knopf>
            <Knopf variante="primaer" className="!text-xs" laedt={arbeitet} disabled={!gewaehlt.length}
                   onClick={() => beiZuordnen(gewaehlt)}>
              Zuordnen
            </Knopf>
          </div>
        </div>
      </div>
    </Blickfang>
  );
}
